import React from "react";
import { Button } from "semantic-ui-react";
import { FORM_SCHEMA_TYPES as TYPES } from "../formSchemaTypes";
import { getZendeskTicketUrl } from "../../../../../../../../constants/endpoints";
import ZendeskIcon from "../../../../../../../../components/Icons/ZendeskIcon/ZendeskIcon";

export const SCHENGEN_SELECT_VALUES = {
  COUNTRIES_V2: "COUNTRIES_V2",
  MARITAL_STATUS: "MARITAL_STATUS_V2",
  GENDERS: "GENDERS_V2",
  TRAVEL_DOCUMENT_TYPE: "TRAVEL_DOCUMENT_TYPE_V2",
  FAMILY_RELATION: "FAMILY_RELATION_V2",
  PURPOSES: "PURPOSES_V2",
  ENTRIES_REQUESTED: "ENTRIES_REQUESTED_V2",
  ACCOMMODATION_DETAILS: "ACCOMMODATION_DETAILS_V2",
  TRIP_PAYER: "TRIP_PAYER_V2",
  EXPENSE_COVERAGE_METHOD: "EXPENSE_COVERAGE_METHOD_V2",
  SECURITY_QUESTIONS: "SECURITY_QUESTIONS_V2",
  OTHER_NATIONALITY_DOC_TYPE: "OTHER_NATIONALITY_DOC_TYPE_V2",
};

export const schengenSchemaNew = {
  generalInfoSegment: {
    label: "General application information",
    type: TYPES.SEGMENT,
    initialOpen: true,
    data: {
      _id: {
        label: "ID",
      },
      applicationType: {
        label: "Application Type",
      },
      mainApplicationId: {
        label: "Main Application ID",
        dependsOn: "applicationType",
        dependantOnValues: ["Secondary"],
      },
      siteId: {
        label: "Referral Site",
      },
      cartSiteId: {
        label: "Purchased from website",
      },
      orderId: {
        label: "Order ID",
      },
      orderIdHistory: {
        label: "Order ID History",
      },
      invoiceEmail: {
        label: "Payment email",
      },
      zendeskId: {
        label: "Zendesk ID",
        type: TYPES.TEXT,
        required: false,
        renderIcon: (propValue, applicant) => (
          <Button
            onClick={() => {
              window.open(
                getZendeskTicketUrl(propValue, applicant?.zendeskInstance),
                "_blank",
                "noopener,noreferrer",
              );
            }}
            style={{ width: 36, height: 36, padding: 0, float: "right" }}
            disabled={!propValue}
          >
            <ZendeskIcon className="zendesk-icon" />
          </Button>
        ),
      },
      submissionEmail: {
        label: "Submission email",
        type: TYPES.TEXT,
      },
      submissionPassword: {
        label: "Submission password",
        type: TYPES.TEXT,
      },
      gclid: {
        label: "Google client ID",
      },
      userLanguage: {
        label: "User form language",
      },
      userCountry: {
        label: "Users IP country",
      },
      salesAgent: {
        label: "Sales Agent",
      },
      salesCoupon: {
        label: "Sales Coupon",
      },
    },
  },
  contactInfoSegment: {
    label: "Contact information",
    type: TYPES.SEGMENT,
    data: {
      firstName: {
        label: "Given name",
        type: TYPES.TEXT,
        required: true,
      },
      lastName: {
        label: "Surname",
        type: TYPES.TEXT,
        required: true,
      },
      surnameAtBirth: {
        label: "Surname at birth",
        type: TYPES.TEXT,
        required: true,
      },
      dateOfBirth: {
        label: "Date of birth",
        type: TYPES.DATE,
        required: true,
      },
      countryOfBirth: {
        label: "Country of birth",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
        required: true,
      },
      placeOfBirth: {
        label: "Place of birth",
        type: TYPES.TEXT,
        required: true,
      },
      userEmail: {
        label: "Email",
        type: TYPES.EMAIL,
        required: true,
      },
      phoneNumber: {
        label: "Phone number",
        type: TYPES.TEXT,
        required: true,
      },
      maritalStatus: {
        label: "Marital status",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.MARITAL_STATUS,
      },
      specifyStatus: {
        label: "Specify status",
        type: TYPES.TEXT,
        dependsOn: "maritalStatus",
        dependantOnValues: ["Other"],
        required: true,
      },
      gender: {
        label: "Gender",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.GENDERS,
        required: true,
      },
    },
  },
  documentInformation: {
    label: "Document information",
    type: TYPES.SEGMENT,
    data: {
      countryOfResidence: {
        label: "Country of residence",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
        required: true,
      },
      currentNationality: {
        label: "Current nationality",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
        required: true,
      },
      travelDocumentType: {
        label: "Travel document type",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.TRAVEL_DOCUMENT_TYPE,
        required: true,
      },
      otherDocumentDetails: {
        label: "Other document details",
        type: TYPES.TEXT,
        dependsOn: "travelDocumentType",
        dependantOnValues: ["Other"],
        required: true,
      },
      passportNumber: {
        label: "Number of travel document",
        type: TYPES.TEXT,
        required: true,
      },
      dateOfIssue: {
        label: "Date of issue",
        type: TYPES.DATE,
        required: true,
      },
      expirationDate: {
        label: "Expiration date",
        type: TYPES.DATE,
        required: true,
      },
      issuedBy: {
        label: "Issues by",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
        required: true,
      },
      differentNationality: {
        label: "Did you hold a different nationality at birth?",
        type: TYPES.CHECKBOX,
        required: true,
      },
      previousNationality: {
        label: "Previous nationality",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
        dependsOn: "differentNationality",
        required: true,
      },
      nationalIdIssued: {
        label: "Does your country issue a national identification number?",
        type: TYPES.CHECKBOX,
        required: true,
      },
      nationalIdNumber: {
        label: "National identification number",
        type: TYPES.TEXT,
        dependsOn: "nationalIdIssued",
        required: true,
      },
      residentOtherCountry: {
        label:
          "Are you a resident in a country other than the country of your current nationality?",
        type: TYPES.CHECKBOX,
        required: true,
      },
      otherNationalityInformation: {
        label: "Other nationality information",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "residentOtherCountry",
        nested: {
          otherNationalityDocType: {
            label: "Document type",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.OTHER_NATIONALITY_DOC_TYPE,
            path: "otherNationalityInformation",
          },
          otherNationalityDocSpecify: {
            label: "Other document type",
            type: TYPES.TEXT,
            dependsOn: "otherNationalityInformation.otherNationalityDocType",
            dependantOnValues: ["Other"],
            path: "otherNationalityInformation",
          },
          otherNationalityDocNumber: {
            label: "Document number",
            type: TYPES.TEXT,
            path: "otherNationalityInformation",
          },
          otherNationalityDocIssueDate: {
            label: "Date of issue",
            type: TYPES.DATE,
            path: "otherNationalityInformation",
          },
          otherNationalityDocExpiryDate: {
            label: "Expiration date",
            type: TYPES.DATE,
            path: "otherNationalityInformation",
          },
        },
      },
    },
  },
  addressInformation: {
    label: "Address information",
    type: TYPES.SEGMENT,
    data: {
      addressLine1: {
        label: "Address line 1",
        type: TYPES.TEXT,
        required: true,
      },
      addressLine2: {
        label: "Address line 2",
        type: TYPES.TEXT,
      },
      country: {
        label: "Country",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
        required: true,
      },
      city: {
        label: "City",
        type: TYPES.TEXT,
        required: true,
      },
      postalCode: {
        label: "Postal / ZIP code",
        type: TYPES.TEXT,
      },
    },
  },
  familyMembersInformation: {
    label: "Family members information",
    type: TYPES.SEGMENT,
    data: {
      hasFamilyEU: {
        label:
          "Does your family members have citizenship of an EU, EEA countries, or Switzerland?",
        type: TYPES.CHECKBOX,
        required: true,
      },
      familyMembers: {
        label: "Family member",
        type: TYPES.ARRAY,
        dependsOn: "hasFamilyEU",
        data: {
          firstName: {
            label: "Given name",
            type: TYPES.TEXT,
            required: true,
            path: "familyMembers.$familyMembers",
          },
          lastName: {
            label: "Surname",
            type: TYPES.TEXT,
            required: true,
            path: "familyMembers.$familyMembers",
          },
          nationality: {
            label: "Current nationality",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            required: true,
            path: "familyMembers.$familyMembers",
          },
          dateOfBirth: {
            label: "Date of birth",
            type: TYPES.DATE,
            required: true,
            path: "familyMembers.$familyMembers",
          },
          travelPassNumber: {
            label: "Number of travel document / ID card",
            type: TYPES.TEXT,
            required: true,
            path: "familyMembers.$familyMembers",
          },
          relationship: {
            label: "Relationship to you",
            type: TYPES.SELECT,
            value: "$value",
            values: SCHENGEN_SELECT_VALUES.FAMILY_RELATION,
            required: true,
            path: "familyMembers.$familyMembers",
          },
        },
      },
    },
  },
  educationInformation: {
    label: "Education information",
    type: TYPES.SEGMENT,
    data: {
      isStudying: {
        label: "Do you currently study at a college or university?",
        type: TYPES.CHECKBOX,
        required: true,
      },
      educationEstablishmentInfo: {
        label: "Educational establishment information",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "isStudying",
        nested: {
          institutionName: {
            label: "Name of the educational establishment",
            type: TYPES.TEXT,
            required: true,
            path: "educationEstablishmentInfo",
          },
          institutionAddressLine1: {
            label: "Address line 1",
            type: TYPES.TEXT,
            required: true,
            path: "educationEstablishmentInfo",
          },
          institutionAddressLine2: {
            label: "Address line 2",
            type: TYPES.TEXT,
            path: "educationEstablishmentInfo",
          },
          institutionCountry: {
            label: "Country",
            type: TYPES.SELECT,
            path: "educationEstablishmentInfo",
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            required: true,
          },
          institutionCity: {
            label: "City",
            type: TYPES.TEXT,
            required: true,
            path: "educationEstablishmentInfo",
          },
          institutionHouseNumber: {
            label: "House number",
            type: TYPES.TEXT,
            path: "educationEstablishmentInfo",
          },
          institutionPostalCode: {
            label: "Postal / ZIP code",
            type: TYPES.TEXT,
            path: "educationEstablishmentInfo",
          },
        },
      },
    },
  },
  employmentInformation: {
    label: "Employment information",
    type: TYPES.SEGMENT,
    data: {
      isEmployed: {
        label: "Do you currently employed?",
        type: TYPES.CHECKBOX,
        required: true,
      },
      employmentDetails: {
        label: "Employment details",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "isEmployed",
        nested: {
          occupation: {
            label: "Current occupation",
            type: TYPES.TEXT,
            required: true,
            path: "employmentDetails",
          },
          employerName: {
            label: "Employer name",
            type: TYPES.TEXT,
            required: true,
            path: "employmentDetails",
          },
          employerPhone: {
            label: "Employer phone number",
            type: TYPES.TEXT,
            required: true,
            path: "employmentDetails",
          },
          employerEmail: {
            label: "Employer email",
            type: TYPES.EMAIL,
            required: true,
            path: "employmentDetails",
          },
          employerAddressLine1: {
            label: "Address line 1",
            type: TYPES.TEXT,
            required: true,
            path: "employmentDetails",
          },
          employerAddressLine2: {
            label: "Address line 2",
            type: TYPES.TEXT,
            path: "employmentDetails",
          },
          employerCountry: {
            label: "Country",
            type: TYPES.SELECT,
            path: "employmentDetails",
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            required: true,
          },
          employerCity: {
            label: "City",
            type: TYPES.TEXT,
            required: true,
            path: "employmentDetails",
          },
          employerHouseNumber: {
            label: "House number",
            type: TYPES.TEXT,
            path: "employmentDetails",
          },
          employerPostalCode: {
            label: "Postal / ZIP code",
            type: TYPES.TEXT,
            required: true,
            path: "employmentDetails",
          },
        },
      },
    },
  },
  previousTripInformation: {
    label: "Trip Information",
    type: TYPES.SEGMENT,
    data: {
      hadSchengenVisaPastThreeYears: {
        label:
          "Have you had Schengen visas issued during the past three years?",
        type: TYPES.CHECKBOX,
        required: true,
      },
      previousSchengenVisaInfo: {
        label: "Previous Schengen visa information",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "hadSchengenVisaPastThreeYears",
        nested: {
          previousVisaNumber: {
            label: "Schengen visa number",
            type: TYPES.TEXT,
            path: "previousSchengenVisaInfo",
          },
          previousVisaIssueDate: {
            label: "Date of issue",
            type: TYPES.DATE,
            path: "previousSchengenVisaInfo",
          },
          previousVisaExpiryDate: {
            label: "Expiration date",
            type: TYPES.DATE,
            path: "previousSchengenVisaInfo",
          },
        },
      },
      fingerprintsCollectedLast59Months: {
        label:
          "Have you had your fingerprints collected previously for the purpose of applying for a Schengen visa in the past59 months?",
        type: TYPES.CHECKBOX,
        required: true,
      },
      fingerprintsCollectionDate: {
        label: "Specify date",
        type: TYPES.DATE,
        dependsOn: "fingerprintsCollectedLast59Months",
        required: true,
      },
    },
  },
  intendedTripInformation: {
    label: "Intended trip information",
    type: TYPES.SEGMENT,
    data: {
      mainPurpose: {
        label: "Main purpose of the journey",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.PURPOSES,
        required: true,
      },
      explainPurpose: {
        label: "Explain purpose of the journey",
        type: TYPES.TEXT,
        dependsOn: "mainPurpose",
        dependantOnValues: ["Other"],
        required: true,
      },
      visaCountry: {
        label: "Schengen country your are applying for visa",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
        required: true,
      },
      firstEntryCountry: {
        label: "Schengen country of first entry",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
        required: true,
      },
      entriesRequested: {
        label: "Numbers of entries requested",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.ENTRIES_REQUESTED,
        required: true,
      },
      plannedTripsThisYear: {
        label: "Planned Schengen trips in the coming year",
        type: TYPES.NUMBER,
        required: true,
      },
      intendedArrivalDate: {
        label: "Intended date of arrival",
        type: TYPES.DATE,
        required: true,
      },
      intendedDepartureDate: {
        label: "Intended date of departure",
        type: TYPES.DATE,
        required: true,
      },
      hasEntryPermit: {
        label: "Do you have entry permit for the final country of destination?",
        type: TYPES.CHECKBOX,
        required: true,
      },
      entryPermitInfo: {
        label: "Entry permit information",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "hasEntryPermit",
        nested: {
          entryPermitIssuedBy: {
            label: "Issued by",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            path: "entryPermitInfo",
          },
          entryPermitIssueDate: {
            label: "Date of issue",
            type: TYPES.DATE,
            path: "entryPermitInfo",
          },
          entryPermitExpiryDate: {
            label: "Expiration date",
            type: TYPES.DATE,
            path: "entryPermitInfo",
          },
        },
      },
      destinationStates: {
        label: "Member state of destination",
        type: TYPES.ARRAY,
        data: {
          destinationCountry: {
            label: "Country of destination",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            required: true,
            path: "destinationStates.$destinationStates",
          },
          stayDurationDays: {
            label: "Duration in days of the intended stay or transit",
            type: TYPES.NUMBER,
            required: true,
            path: "destinationStates.$destinationStates",
          },
        },
      },
    },
  },
  invitingEntityInformation: {
    label: "Inviting entity information",
    type: TYPES.SEGMENT,
    data: {
      accommodationDetails: {
        label: "Accommodation details",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.ACCOMMODATION_DETAILS,
        required: true,
      },
      companyInfo: {
        label: "Company / organization information",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "accommodationDetails",
        dependantOnValues: ["Inviting company / organization"],
        nested: {
          companyName: {
            label: "Company / organization name",
            type: TYPES.TEXT,
            path: "companyInfo",
          },
          companyAddressLine1: {
            label: "Address line 1",
            type: TYPES.TEXT,
            path: "companyInfo",
          },
          companyAddressLine2: {
            label: "Address line 2",
            type: TYPES.TEXT,
            path: "companyInfo",
          },
          companyCountry: {
            label: "Country",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            path: "companyInfo",
          },
          companyCity: {
            label: "City",
            type: TYPES.TEXT,
            path: "companyInfo",
          },
          companyHouseNumber: {
            label: "House number",
            type: TYPES.TEXT,
            path: "companyInfo",
          },
          companyPostalCode: {
            label: "Postal / ZIP code",
            type: TYPES.TEXT,
            path: "companyInfo",
          },
          stayAddressDifferent: {
            label:
              "Is the stay address different from the inviting entity's address in the member state(s)?",
            type: TYPES.CHECKBOX,
            required: true,
            path: "companyInfo",
          },
          stayAddressInfo: {
            label: "Stay address information",
            type: TYPES.NESTED,
            newTable: true,
            dependsOn: "companyInfo.stayAddressDifferent",
            nested: {
              stayAddressLine1: {
                label: "Address line 1",
                type: TYPES.TEXT,
                required: true,
                path: "companyInfo.stayAddressInfo",
              },
              stayAddressLine2: {
                label: "Address line 2",
                type: TYPES.TEXT,
                path: "companyInfo.stayAddressInfo",
              },
              stayCountry: {
                label: "Country",
                type: TYPES.SELECT,
                values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
                required: true,
                path: "companyInfo.stayAddressInfo",
              },
              stayCity: {
                label: "City",
                type: TYPES.TEXT,
                path: "companyInfo.stayAddressInfo",
              },
              stayHouseNumber: {
                label: "House number",
                type: TYPES.TEXT,
                path: "companyInfo.stayAddressInfo",
              },
              stayPostalCode: {
                label: "Postal / ZIP code",
                type: TYPES.TEXT,
                path: "companyInfo.stayAddressInfo",
              },
            },
          },
        },
      },
      contactPersonInfo: {
        label: "Contact person information",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "accommodationDetails",
        dependantOnValues: ["Inviting company / organization"],
        nested: {
          contactGender: {
            label: "Gender",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.GENDERS,
            path: "contactPersonInfo",
          },
          contactFirstName: {
            label: "Given name",
            type: TYPES.TEXT,
            path: "contactPersonInfo",
            required: true,
          },
          contactLastName: {
            label: "Surname",
            type: TYPES.TEXT,
            path: "contactPersonInfo",
            required: true,
          },
          contactPhone: {
            label: "Phone number",
            type: TYPES.TEXT,
            path: "contactPersonInfo",
            required: true,
          },
          contactEmail: {
            label: "Email",
            type: TYPES.EMAIL,
            path: "contactPersonInfo",
            required: true,
          },
          contactAddressLine1: {
            label: "Address line 1",
            type: TYPES.TEXT,
            required: true,
            path: "contactPersonInfo",
          },
          contactAddressLine2: {
            label: "Address line 2",
            type: TYPES.TEXT,
            path: "contactPersonInfo",
          },
          contactCountry: {
            label: "Country",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            required: true,
            path: "contactPersonInfo",
          },
          contactCity: {
            label: "City",
            type: TYPES.TEXT,
            required: true,
            path: "contactPersonInfo",
          },
          contactHouseNumber: {
            label: "House number",
            type: TYPES.TEXT,
            path: "contactPersonInfo",
          },
          contactPostalCode: {
            label: "Postal / ZIP code",
            type: TYPES.TEXT,
            required: true,
            path: "contactPersonInfo",
          },
        },
      },
      invitingPersonInfo: {
        label: "Inviting person(s) in the member state(s) information",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "accommodationDetails",
        dependantOnValues: ["Inviting person(s) in the member state(s)"],
        nested: {
          invitingPersonGender: {
            label: "Gender",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.GENDERS,
            required: true,
            path: "invitingPersonInfo",
          },
          invitingPersonFirstName: {
            label: "Given name",
            type: TYPES.TEXT,
            required: true,
            path: "invitingPersonInfo",
          },
          invitingPersonLastName: {
            label: "Surname",
            type: TYPES.TEXT,
            required: true,
            path: "invitingPersonInfo",
          },
          invitingPersonPhone: {
            label: "Phone number",
            type: TYPES.TEXT,
            required: true,
            path: "invitingPersonInfo",
          },
          invitingPersonEmail: {
            label: "Email",
            type: TYPES.EMAIL,
            required: true,
            path: "invitingPersonInfo",
          },
          invitingPersonAddressLine1: {
            label: "Address line 1",
            type: TYPES.TEXT,
            required: true,
            path: "invitingPersonInfo",
          },
          invitingPersonAddressLine2: {
            label: "Address line 2",
            type: TYPES.TEXT,
            path: "invitingPersonInfo",
          },
          invitingPersonCountry: {
            label: "Country",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            required: true,
            path: "invitingPersonInfo",
          },
          invitingPersonCity: {
            label: "City",
            type: TYPES.TEXT,
            required: true,
            path: "invitingPersonInfo",
          },
          invitingPersonHouseNumber: {
            label: "House number",
            type: TYPES.TEXT,
            path: "invitingPersonInfo",
          },
          invitingPersonPostalCode: {
            label: "Postal / ZIP code",
            type: TYPES.TEXT,
            required: true,
            path: "invitingPersonInfo",
          },
          stayAddressDifferent: {
            label:
              "Is the stay address different from the inviting entity's address in the member state(s)?",
            type: TYPES.CHECKBOX,
            required: true,
            path: "invitingPersonInfo",
          },
          stayAddressInfo: {
            label: "Stay address information",
            type: TYPES.NESTED,
            newTable: true,
            dependsOn: "invitingPersonInfo.stayAddressDifferent",
            nested: {
              stayAddressLine1: {
                label: "Address line 1",
                type: TYPES.TEXT,
                required: true,
                path: "invitingPersonInfo.stayAddressInfo",
              },
              stayAddressLine2: {
                label: "Address line 2",
                type: TYPES.TEXT,
                path: "invitingPersonInfo.stayAddressInfo",
              },
              stayCountry: {
                label: "Country",
                type: TYPES.SELECT,
                values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
                required: true,
                path: "invitingPersonInfo.stayAddressInfo",
              },
              stayCity: {
                label: "City",
                type: TYPES.TEXT,
                required: true,
                path: "invitingPersonInfo.stayAddressInfo",
              },
              stayHouseNumber: {
                label: "House number",
                type: TYPES.TEXT,
                path: "invitingPersonInfo.stayAddressInfo",
              },
              stayPostalCode: {
                label: "Postal / ZIP code",
                type: TYPES.TEXT,
                required: true,
                path: "invitingPersonInfo.stayAddressInfo",
              },
            },
          },
        },
      },
      hotelInfo: {
        label: "Hotel(s) or temporary accommodation(s) in the member state",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "accommodationDetails",
        dependantOnValues: [
          "Hotel(s) or temporary accommodation(s) in the member state",
        ],
        nested: {
          hotelName: {
            label: "Name of hotel or temporary accommodation",
            type: TYPES.TEXT,
            required: true,
            path: "hotelInfo",
          },
          hotelAddressLine1: {
            label: "Address line 1",
            type: TYPES.TEXT,
            required: true,
            path: "hotelInfo",
          },
          hotelAddressLine2: {
            label: "Address line 2",
            type: TYPES.TEXT,
            path: "hotelInfo",
          },
          hotelCountry: {
            label: "Country",
            type: TYPES.SELECT,
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            required: true,
            path: "hotelInfo",
          },
          hotelCity: {
            label: "City",
            type: TYPES.TEXT,
            required: true,
            path: "hotelInfo",
          },
          hotelHouseNumber: {
            label: "House number",
            type: TYPES.TEXT,
            path: "hotelInfo",
          },
          hotelPostalCode: {
            label: "Postal / ZIP code",
            type: TYPES.TEXT,
            required: true,
            path: "hotelInfo",
          },
          stayAddressDifferent: {
            label:
              "Is the stay address different from the inviting entity's address in the member state(s)?",
            type: TYPES.CHECKBOX,
            required: true,
            path: "hotelInfo",
          },
          stayAddressInfo: {
            label: "Stay address information",
            type: TYPES.NESTED,
            newTable: true,
            dependsOn: "hotelInfo.stayAddressDifferent",
            nested: {
              stayAddressLine1: {
                label: "Address line 1",
                type: TYPES.TEXT,
                required: true,
                path: "hotelInfo.stayAddressInfo",
              },
              stayAddressLine2: {
                label: "Address line 2",
                type: TYPES.TEXT,
                path: "hotelInfo.stayAddressInfo",
              },
              stayCountry: {
                label: "Country",
                type: TYPES.SELECT,
                values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
                required: true,
                path: "hotelInfo.stayAddressInfo",
              },
              stayCity: {
                label: "City",
                type: TYPES.TEXT,
                required: true,
                path: "hotelInfo.stayAddressInfo",
              },
              stayHouseNumber: {
                label: "House number",
                type: TYPES.TEXT,
                path: "hotelInfo.stayAddressInfo",
              },
              stayPostalCode: {
                label: "Postal / ZIP code",
                type: TYPES.TEXT,
                required: true,
                path: "hotelInfo.stayAddressInfo",
              },
            },
          },
        },
      },
    },
  },
  paymentInformation: {
    label: "Payment information",
    type: TYPES.SEGMENT,
    data: {
      tripPayer: {
        label: "Who will be paying for your trip?",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.TRIP_PAYER,
        required: true,
      },
      expenseCoverageMethod: {
        label: "Method of covering your expenses",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.EXPENSE_COVERAGE_METHOD,
        required: true,
        dependsOn: "tripPayer",
        dependantOnValues: ["Yourself", "Guarantor"],
      },
      useOtherMethodsSelf: {
        label: "Do you use other methods of covering your expenses?",
        type: TYPES.CHECKBOX,
        dependsOn: "tripPayer",
        dependantOnValues: ["Yourself"],
        required: true,
      },
      specifyOtherMethodsSelf: {
        label: "Specify details",
        type: TYPES.TEXT,
        dependsOn: "useOtherMethodsSelf",
      },
      useOtherMethodsGuarantor: {
        label: "Does guarantor use other methods of covering your expenses?",
        type: TYPES.CHECKBOX,
        dependsOn: "tripPayer",
        dependantOnValues: ["Guarantor"],
        required: true,
      },
      guarantorInfo: {
        label: "Guarantor information",
        type: TYPES.NESTED,
        newTable: true,
        dependsOn: "tripPayer",
        dependantOnValues: ["Guarantor"],
        nested: {
          guarantorFirstName: {
            label: "Given name",
            type: TYPES.TEXT,
            required: true,
            path: "guarantorInfo",
          },
          guarantorLastName: {
            label: "Surname",
            type: TYPES.TEXT,
            required: true,
            path: "guarantorInfo",
          },
          guarantorPhone: {
            label: "Phone number",
            type: TYPES.TEXT,
            required: true,
            path: "guarantorInfo",
          },
          guarantorEmail: {
            label: "Email",
            type: TYPES.EMAIL,
            required: true,
            path: "guarantorInfo",
          },
          guarantorAddressLine1: {
            label: "Address line 1",
            type: TYPES.TEXT,
            required: true,
            path: "guarantorInfo",
          },
          guarantorAddressLine2: {
            label: "Address line 2",
            type: TYPES.TEXT,
            path: "guarantorInfo",
          },
          guarantorCountry: {
            label: "Country",
            type: TYPES.SELECT,
            path: "guarantorInfo",
            values: SCHENGEN_SELECT_VALUES.COUNTRIES_V2,
            required: true,
          },
          guarantorCity: {
            label: "City",
            type: TYPES.TEXT,
            required: true,
            path: "guarantorInfo",
          },
          guarantorHouseNumber: {
            label: "House number",
            type: TYPES.TEXT,
            path: "guarantorInfo",
          },
          guarantorPostalCode: {
            label: "Postal / ZIP code",
            type: TYPES.TEXT,
            required: true,
            path: "guarantorInfo",
          },
        },
      },
      specifyOtherMethodsOther: {
        label: "Specify details",
        type: TYPES.TEXT,
        required: true,
        dependsOn: "tripPayer",
        dependantOnValues: ["Other"],
      },
    },
  },
  securityQuestions: {
    label: "Security questions",
    type: TYPES.SEGMENT,
    data: {
      securityQuestion: {
        label: "Security question",
        type: TYPES.SELECT,
        values: SCHENGEN_SELECT_VALUES.SECURITY_QUESTIONS,
        required: true,
      },
      securityAnswer: {
        label: "Answer",
        type: TYPES.TEXT,
        required: true,
      },
      applicantSignature: {
        label: "Signature of applicant",
        type: TYPES.TEXT,
        required: true,
      },
      agreeConsent: {
        label: "Agree with Consent and Declaration",
        type: TYPES.CHECKBOX,
        required: true,
      },
      agreeTerms: {
        label: "Agree with Terms & Conditions",
        type: TYPES.CHECKBOX,
        required: true,
      },
    },
  },
  personalDataConfidentiality: {
    label: "Personal data confidentiality & protection",
    type: TYPES.SEGMENT,
    data: {
      guardianName: {
        label: "Parent / Guardian's Name",
        type: TYPES.TEXT,
        required: true,
      },
      guardianSignature: {
        label: "Signature of parent or guardian",
        type: TYPES.TEXT,
        required: true,
      },
    },
  },
};
